import { format, pickVariant } from '@/i18n/format'
import type { LocaleCode } from '@/i18n/locales'
import { DEFAULT_LOCALE } from '@/i18n/locales'
import type { Profile, SlotId, SlotSource } from '@/profile/types'
import { CONFIRMATION_THRESHOLD, emptyProfile, makeSlot } from '@/profile/types'
import { extractAffirmation } from '@/voice/nlu/extract'
import { readReplyLocale } from './detectLocale'
import { isDeferral } from './escapes'
import type { OnboardingScript } from './script'
import { ONBOARDING_SCRIPT } from './script'
import type { OnboardingStep, OnboardingStepId, StepContext } from './steps'
import { findStep } from './steps'
import type { VolunteeredFacts } from './volunteered'

/**
 * The onboarding conversation, as a state machine.
 *
 * Nothing in here awaits anything. Events go in, a list of things to do comes
 * out, and the whole state is a plain object that survives `JSON.stringify`.
 * That is the difference between this and the async director it replaces, and
 * it buys two things that matter more than the elegance.
 *
 * Resumability is the first. A conversation that lives in the call stack of an
 * `await` loop dies with the tab, so the old flow's answer to a dropped
 * connection was to start again from the greeting. Here the state is a value:
 * write it down, read it back, carry on from the step it was on.
 *
 * Testability is the second. A pure engine means the entire conversation,
 * including the paths nobody exercises by hand, can be asserted without a
 * browser, a microphone or a clock. `engine.test.ts` runs a person through
 * declining consent, correcting a misheard name and failing three times into
 * the keyboard, in both languages, in a few milliseconds.
 *
 * The host is responsible for turning effects into speech and microphone
 * turns, and for feeding results back in. It is deliberately not responsible
 * for deciding anything.
 */

export type SettleReason =
  /** Everything was asked and answered. */
  | 'complete'
  /** Consent was refused. A complete path, not a dead end. */
  | 'declined'
  /** Consent was deferred or never heard, so there is nothing to store. */
  | 'unanswered'
  /** The person walked away. What was captured is captured. */
  | 'abandoned'

export type OnboardingEffect =
  | { kind: 'say'; text: string; interruptible: boolean }
  /** Open the microphone for one turn on behalf of this step. */
  | { kind: 'listen'; stepId: OnboardingStepId }
  /** Put the disclosure on screen while it is being spoken. */
  | { kind: 'disclose'; points: readonly string[] }
  /** Show the keyboard. Voice-first is not voice-only. */
  | { kind: 'offerTyped'; stepId: OnboardingStepId }
  /** Write this now, locally and to the server. See `writer.ts`. */
  | { kind: 'persist'; slot: SlotId; profile: Profile }
  /** The assistant takes the microphone. Onboarding is not asking anything. */
  | { kind: 'handover'; profile: Profile }
  /** Onboarding will ask nothing further. */
  | { kind: 'settle'; reason: SettleReason; profile: Profile }

export type OnboardingEvent =
  /** The tap. Nothing can make a sound before it, because no browser allows it. */
  | { type: 'begin' }
  | { type: 'heard'; transcript: string; confidence?: number; typed?: boolean }
  /** The person never started talking. */
  | { type: 'silence' }
  /** Something was heard and could not be turned into words. */
  | { type: 'unintelligible' }
  /** The open conversation answered a question well. Place is now earned. */
  | { type: 'served' }
  /** The one control on screen. */
  | { type: 'skip' }
  | { type: 'abandon' }

type Stage = 'idle' | 'awaiting-answer' | 'awaiting-confirmation' | 'serving' | 'settled'

/**
 * Why the previous attempt failed, which decides how the next one is phrased.
 *
 * The distinction that earns its keep is silence against noise. "Sorry, I did
 * not catch that" is right for somebody whose words were garbled and wrong for
 * somebody who never spoke, where it is an accusation about an answer they did
 * not give.
 */
type RepairReason = 'none' | 'no-input' | 'no-match'

export interface OnboardingSnapshot {
  /** Bumped when the shape changes, so a stale snapshot is discarded not misread. */
  version: number
  stepId: OnboardingStepId
  stage: Stage
  attempt: number
  typedOffered: boolean
  /** A value said back and waiting for a yes or a no. */
  pending: { value: string; raw: string } | null
  locale: LocaleCode
  profile: Profile
  /**
   * Variant counters, carried in the snapshot rather than held in a rotator.
   *
   * Without this, a conversation resumed after a dropped connection restarts
   * every rotation at zero and says the same phrasing it said a minute ago,
   * which is precisely the moment the repetition is most noticeable.
   */
  rotations: Record<string, number>
  lastFailure: RepairReason
  settled: SettleReason | null
}

export const SNAPSHOT_VERSION = 1

export interface OnboardingOptions {
  /** Whatever the app is currently in. The answer for silence. */
  deviceLocale?: LocaleCode
  /** What is already on the device, so a second run does not re-ask. */
  profile?: Profile
  script?: Record<LocaleCode, OnboardingScript>
  maxAttempts?: number
  now?: () => number
}

/**
 * Three attempts, then the keyboard.
 *
 * Every voice-design guide converges on three and the reason is not arbitrary:
 * being asked to repeat yourself is the single most disliked thing about
 * talking to a machine. What is added here is that the fourth attempt is not a
 * fourth attempt at speaking. Somebody in a loud market has not failed to
 * answer, they have failed to be heard, and asking a fourth time is asking
 * them to shout.
 */
const DEFAULT_MAX_ATTEMPTS = 3

/**
 * Below this, an answer is thrown away and the question asked again rather
 * than read back.
 *
 * Confirming a value that is probably wrong spends a turn on the way to asking
 * again anyway, and it puts a wrong word in the person's ear. Carried over
 * from the removed director, where it was tuned against the fact that the
 * number being tested is the product of two independent confidences, the
 * recogniser's and the parser's, and a product is always the smaller of the
 * pair.
 */
const REJECT_BELOW = 0.35

export class OnboardingFlow {
  private readonly options: Required<Pick<OnboardingOptions, 'maxAttempts'>> & OnboardingOptions
  private readonly scripts: Record<LocaleCode, OnboardingScript>
  private readonly deviceLocale: LocaleCode
  private state: OnboardingSnapshot

  constructor(options: OnboardingOptions = {}) {
    // Spread first, then the default. The other order lets an explicitly
    // absent `maxAttempts` key overwrite the default with undefined, which
    // makes every comparison against it false and turns the retry budget off.
    this.options = { ...options, maxAttempts: options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS }
    this.scripts = options.script ?? ONBOARDING_SCRIPT
    this.deviceLocale = options.deviceLocale ?? DEFAULT_LOCALE
    const profile = options.profile ? { ...options.profile } : emptyProfile()
    this.state = {
      version: SNAPSHOT_VERSION,
      stepId: entryStep(profile),
      stage: 'idle',
      attempt: 1,
      typedOffered: false,
      pending: null,
      locale: profile.language?.value ?? this.deviceLocale,
      profile,
      rotations: {},
      lastFailure: 'none',
      settled: null,
    }
  }

  /**
   * Picks a conversation back up exactly where it stopped.
   *
   * A snapshot from an older build is refused rather than coerced. A flow
   * resumed into a step that no longer exists is a conversation that asks
   * nothing and never ends, which is worse than starting again.
   */
  static resume(snapshot: OnboardingSnapshot, options: OnboardingOptions = {}): OnboardingFlow {
    const flow = new OnboardingFlow({ ...options, profile: snapshot.profile })
    if (snapshot.version === SNAPSHOT_VERSION) {
      flow.state = { ...snapshot, profile: { ...snapshot.profile } }
    }
    return flow
  }

  get snapshot(): OnboardingSnapshot {
    return { ...this.state, profile: { ...this.state.profile } }
  }

  get profile(): Profile {
    return this.state.profile
  }

  get locale(): LocaleCode {
    return this.state.locale
  }

  get stepId(): OnboardingStepId {
    return this.state.stepId
  }

  /** Null while onboarding is still running. */
  get settled(): SettleReason | null {
    return this.state.settled
  }

  /** True when the keyboard has been offered for the current step. */
  get typedOffered(): boolean {
    return this.state.typedOffered
  }

  receive(event: OnboardingEvent): readonly OnboardingEffect[] {
    switch (event.type) {
      case 'begin':
        return this.begin()
      case 'heard':
        return this.onHeard(event.transcript, event.confidence ?? 1, event.typed === true)
      case 'silence':
        return this.onFailure('no-input')
      case 'unintelligible':
        return this.onFailure('no-match')
      case 'served':
        return this.onServed()
      case 'skip':
        return this.onSkip()
      case 'abandon':
        return this.settle('abandoned')
    }
  }

  /**
   * Records something offered rather than asked for.
   *
   * Never overwrites. A value somebody set deliberately in Settings has to win
   * over one inferred from a passing remark, or the assistant argues with its
   * own user about a fact only one of them stated on purpose.
   */
  accept(facts: VolunteeredFacts): readonly OnboardingEffect[] {
    const effects: OnboardingEffect[] = []
    const now = this.now()

    const age = facts.ageBand
    if (age?.value != null && this.state.profile.ageBand === null) {
      this.state.profile.ageBand = makeSlot(age.value, 'spoken', age.confidence, age.raw, now)
      effects.push(this.persist('ageBand'))
    }

    const gender = facts.gender
    if (gender?.value != null && this.state.profile.gender === null) {
      this.state.profile.gender = makeSlot(gender.value, 'spoken', gender.confidence, gender.raw, now)
      effects.push(this.persist('gender'))
    }

    return effects
  }

  // -- the conversation -----------------------------------------------------

  private begin(): readonly OnboardingEffect[] {
    if (this.state.settled !== null) return []
    // Re-entering the current step is what resumability means in practice: a
    // dropped connection returns to the question it was on, not to the
    // greeting. A read-back in flight is dropped rather than restored, because
    // asking somebody to confirm a name they cannot remember hearing is worse
    // than asking for the name again.
    return this.enter(this.state.stepId)
  }

  private enter(stepId: OnboardingStepId): readonly OnboardingEffect[] {
    const step = findStep(stepId)
    this.state.stepId = stepId
    this.state.attempt = 1
    this.state.typedOffered = false
    this.state.pending = null
    this.state.lastFailure = 'none'

    const effects: OnboardingEffect[] = []
    if (stepId === 'consent') {
      effects.push({ kind: 'disclose', points: this.script.consentPoints })
    }
    effects.push(this.say(step.ask(this.context()), { interruptible: true }))

    if (step.listens) {
      this.state.stage = 'awaiting-answer'
      effects.push({ kind: 'listen', stepId })
    } else {
      this.state.stage = 'serving'
      effects.push({ kind: 'handover', profile: this.state.profile })
    }
    return effects
  }

  private onHeard(
    transcript: string,
    heardConfidence: number,
    typed: boolean,
  ): readonly OnboardingEffect[] {
    if (this.state.settled !== null) return []

    // The language step is read differently from every other step: silence and
    // noise are answers to it, and it is never asked twice.
    if (this.state.stepId === 'language') return this.settleLanguage(transcript)

    if (this.state.stage === 'awaiting-confirmation') {
      return this.onConfirmation(transcript, heardConfidence, typed)
    }
    if (this.state.stage !== 'awaiting-answer') return []

    const step = findStep(this.state.stepId)

    // Checked before the step's own parser, always. `extractName` handed
    // "later" returns "Later" at a confidence high enough to keep, and then
    // greets somebody as Later for a year.
    if (isDeferral(transcript, this.locale)) return this.onSkip()

    const parsed = step.parse?.(transcript, this.locale)
    if (!parsed || parsed.value === null) return this.onFailure('no-match')

    // The recogniser's confidence and the parser's are different claims and
    // both have to hold. Multiplying stops a clean parse of a badly heard
    // phrase from looking certain. A typed answer has no recogniser in it.
    const confidence = typed ? parsed.confidence : parsed.confidence * heardConfidence

    if (!typed && confidence < REJECT_BELOW) return this.onFailure('no-match')

    const needsReadBack =
      !typed &&
      step.readBack !== undefined &&
      (step.confirm === 'always' ||
        (step.confirm === 'when-unsure' && confidence < CONFIRMATION_THRESHOLD))

    if (needsReadBack) return this.readBack(step, step.describe(parsed.value), parsed.raw)

    return this.keep(step, parsed.value, typed ? 'typed' : 'spoken', confidence, parsed.raw)
  }

  private onConfirmation(
    transcript: string,
    heardConfidence: number,
    typed: boolean,
  ): readonly OnboardingEffect[] {
    const step = findStep(this.state.stepId)
    const pending = this.state.pending
    if (!pending) return this.enter(this.state.stepId)

    // "Skip" answered to "Amina, have I got that right?" is not a comment on
    // the name. It is somebody asking to stop being asked, and it is honoured
    // here rather than being spent as one more failed attempt at the name.
    if (isDeferral(transcript, this.locale)) {
      this.state.pending = null
      this.state.stage = 'awaiting-answer'
      return this.onSkip()
    }

    const answer = extractAffirmation(transcript, this.locale)
    if (answer.value === 'yes') {
      // Confidence 1 and source `confirmed`: the person heard the value said
      // back in their own language and agreed to it, which is a stronger claim
      // than any number the parser produced.
      return this.keep(step, pending.value, 'confirmed', 1, pending.raw)
    }

    if (answer.value !== 'no' && answer.value !== 'skip') {
      /*
       * Not a yes and not a no. Somebody correcting a misheard name says the
       * name again, they do not say "no"; treating that as an unparseable
       * answer made the assistant apologise and ask a question the person had
       * just answered twice.
       */
      const parsed = step.parse?.(transcript, this.locale)
      if (parsed && parsed.value !== null) {
        const spoken = step.describe(parsed.value)
        if (spoken === pending.value) {
          // Said back identically. That is agreement, whatever words were used.
          return this.keep(step, pending.value, 'confirmed', 1, pending.raw)
        }
        if (this.state.attempt < this.options.maxAttempts) {
          this.state.attempt += 1
          return this.readBack(step, spoken, parsed.raw)
        }
        // Out of attempts, but a value was heard twice and the second one is
        // better evidence than nothing. Kept as spoken rather than confirmed,
        // so the profile screen still shows it as worth checking.
        const confidence = typed ? parsed.confidence : parsed.confidence * heardConfidence
        return this.keep(step, parsed.value, typed ? 'typed' : 'spoken', confidence, parsed.raw)
      }
    }

    // A rejection. Ask again, and spend an attempt doing it.
    this.state.pending = null
    this.state.stage = 'awaiting-answer'
    if (this.state.attempt >= this.options.maxAttempts) return this.offerKeyboardOrGiveUp(step)

    this.state.attempt += 1
    const context = this.context()
    const line = step.reask ? step.reask(context) : this.rotate('didNotCatch', this.script.didNotCatch)
    return [
      this.say(`${line} ${step.ask(context)}`, { interruptible: true }),
      { kind: 'listen', stepId: step.id },
    ]
  }

  /**
   * Says a value back and waits for a yes or a no.
   *
   * Not interruptible: a read-back cut off halfway is a name said wrong and
   * then abandoned, which is exactly the failure the name step exists to
   * prevent.
   */
  private readBack(step: OnboardingStep, spoken: string, raw: string): readonly OnboardingEffect[] {
    const line = step.readBack
    if (!line) return this.enter(step.id)
    this.state.pending = { value: spoken, raw }
    this.state.stage = 'awaiting-confirmation'
    return [
      this.say(line(this.context(), spoken), { interruptible: false }),
      { kind: 'listen', stepId: step.id },
    ]
  }

  private onFailure(reason: RepairReason): readonly OnboardingEffect[] {
    if (this.state.settled !== null) return []
    if (this.state.stage === 'serving' || this.state.stage === 'idle') return []

    /*
     * Silence at the opening is an answer, not a failure.
     *
     * Somebody who says nothing gets the device's own language and can change
     * it in a sentence at any point. Retrying here would be the language menu
     * arriving by the back door: ask, hear nothing, ask again, and by the
     * third attempt the only sensible thing left to say is "Hausa or English".
     */
    if (this.state.stepId === 'language') return this.settleLanguage('')

    const step = findStep(this.state.stepId)
    this.state.lastFailure = reason
    this.state.pending = null
    this.state.stage = 'awaiting-answer'

    if (this.state.attempt < this.options.maxAttempts) {
      this.state.attempt += 1
      return [
        this.say(this.promptAfter(step, reason), { interruptible: true }),
        { kind: 'listen', stepId: step.id },
      ]
    }

    return this.offerKeyboardOrGiveUp(step)
  }

  /**
   * The line that opens a repeat attempt.
   *
   * After silence the question is repeated word for word, because the person
   * heard nothing wrong: they did not answer, and a rephrase suggests
   * otherwise. The second silence gets a gentle check first, because by then
   * the likeliest explanation is that nobody is there.
   */
  private promptAfter(step: OnboardingStep, reason: RepairReason): string {
    const context = this.context()
    const question = step.ask(context)
    if (reason === 'no-input') {
      return this.state.attempt === 2
        ? `${this.rotate('stillThere', this.script.stillThere)} ${question}`
        : question
    }
    return `${this.rotate('didNotCatch', this.script.didNotCatch)} ${question}`
  }

  private offerKeyboardOrGiveUp(step: OnboardingStep): readonly OnboardingEffect[] {
    if (!this.state.typedOffered) {
      this.state.typedOffered = true
      this.state.attempt += 1
      return [
        { kind: 'offerTyped', stepId: step.id },
        this.say(
          `${this.rotate('typedFallback', this.script.typedFallback)} ${step.ask(this.context())}`,
          { interruptible: true },
        ),
        { kind: 'listen', stepId: step.id },
      ]
    }
    return this.giveUp(step)
  }

  /**
   * Moves past a step nobody managed to answer.
   *
   * Nothing is guessed and nothing is treated as a refusal. An unanswered
   * consent question is not a decline: it leaves the profile unfinished, so it
   * is asked another day rather than settled against the person's interest.
   */
  private giveUp(step: OnboardingStep): readonly OnboardingEffect[] {
    switch (step.id) {
      case 'consent':
        return [
          this.say(this.rotate('consentUnasked', this.script.consentUnasked), {
            interruptible: true,
          }),
          ...this.enter('open'),
          ...this.settle('unanswered'),
        ]
      case 'name':
        return this.enter('open')
      case 'place':
        return this.finish()
      default:
        return this.enter('open')
    }
  }

  private onSkip(): readonly OnboardingEffect[] {
    if (this.state.settled !== null) return []
    // Nothing is being asked during the open conversation, so there is nothing
    // to skip. The control should not be on screen at all here.
    if (this.state.stage === 'serving' || this.state.stage === 'idle') return []

    // A skip at the opening is not a refusal of the language, it is somebody
    // who does not want to be asked anything. They get the device's language
    // and the conversation carries on.
    if (this.state.stepId === 'language') return this.settleLanguage('')

    const acknowledgement = this.say(this.rotate('skipped', this.script.skipped), {
      interruptible: false,
    })

    switch (this.state.stepId) {
      case 'consent':
        return [
          acknowledgement,
          this.say(this.rotate('consentUnasked', this.script.consentUnasked), {
            interruptible: true,
          }),
          ...this.enter('open'),
          ...this.settle('unanswered'),
        ]
      case 'name':
        return [acknowledgement, ...this.enter('open')]
      case 'place':
        return [acknowledgement, ...this.finish()]
      default:
        return [acknowledgement, ...this.enter('open')]
    }
  }

  private onServed(): readonly OnboardingEffect[] {
    if (this.state.settled !== null) return []
    if (this.state.stage !== 'serving') return []
    // Asked once. Somebody who declined to give a place and then asks a second
    // question is not being asked a second time.
    if (this.state.profile.region !== null) return []
    return this.enter('place')
  }

  // -- writing --------------------------------------------------------------

  private keep(
    step: OnboardingStep,
    value: unknown,
    source: SlotSource,
    confidence: number,
    raw: string,
  ): readonly OnboardingEffect[] {
    step.commit(this.state.profile, value, source, confidence, raw, this.now())
    const effects: OnboardingEffect[] = step.slot ? [this.persist(step.slot)] : []

    switch (step.id) {
      case 'consent': {
        if (value === true) {
          effects.push(
            this.say(this.rotate('consentAccepted', this.script.consentAccepted), {
              interruptible: false,
            }),
          )
          return [...effects, ...this.enter('name')]
        }
        /*
         * Declining is a complete path, not a dead end. Namu works, it simply
         * does not remember, and it says so once rather than nagging.
         *
         * The name is not asked afterwards, and that is deliberate. Asking for
         * something we have just promised not to keep, in order to throw it
         * away one sentence later, is worse than not asking: it looks like the
         * refusal was not heard.
         */
        effects.push(
          this.say(this.rotate('consentDeclined', this.script.consentDeclined), {
            interruptible: true,
          }),
        )
        return [...effects, ...this.enter('open'), ...this.settle('declined')]
      }

      case 'name':
        /*
         * No acknowledgement here, on purpose. The very next sentence is the
         * invitation and the invitation carries the name, which is the moment
         * the product proves it was listening. Saying "got it" first spends
         * that moment on a word that could have been said to anybody.
         */
        return [...effects, ...this.enter('open')]

      case 'place':
        effects.push(
          this.say(format(this.rotate('placeThanks', this.script.placeThanks), { place: step.describe(value) }), {
            interruptible: false,
          }),
        )
        return [...effects, ...this.finish()]

      default:
        effects.push(
          this.say(this.rotate('acknowledge', this.script.acknowledge), { interruptible: false }),
        )
        return effects
    }
  }

  /** The language step, which is settled by whatever came back, including nothing. */
  private settleLanguage(transcript: string): readonly OnboardingEffect[] {
    const step = findStep('language')
    const reading = readReplyLocale(transcript, this.deviceLocale)
    this.state.locale = reading.locale

    const source: SlotSource =
      reading.basis === 'named' ? 'chosen' : reading.basis === 'spoken' ? 'spoken' : 'inferred'
    step.commit(this.state.profile, reading.locale, source, reading.confidence, transcript, this.now())

    return [this.persist('language'), ...this.enter('consent')]
  }

  /**
   * The end of the questions, whether or not the last one was answered.
   *
   * A skipped or unheard place still finishes rather than leaving onboarding
   * open, and that is a decision about nagging rather than about data. Leaving
   * it unfinished means the same optional question arrives at the start of
   * every future session, which is what "everything else, not in the way" is
   * there to rule out. It stays changeable from Settings and from the
   * conversation itself.
   */
  private finish(): readonly OnboardingEffect[] {
    return [
      this.say(this.rotate('finished', this.script.finished), { interruptible: true }),
      { kind: 'handover', profile: this.state.profile },
      ...this.settle('complete'),
    ]
  }

  /**
   * Marks onboarding as over, and decides whether it will ever be raised again.
   *
   * `completedAt` is set for the two outcomes the person chose: they finished,
   * or they said no. It is left null for the two they did not: they walked
   * away, or nobody could hear them. That is what makes "abandoned halfway and
   * came back" ask the rest of the questions another day, while "no thank you"
   * is never asked again.
   */
  private settle(reason: SettleReason): readonly OnboardingEffect[] {
    if (this.state.settled !== null) return []
    if (reason === 'complete' || reason === 'declined') {
      this.state.profile.completedAt = this.now()
    }
    this.state.settled = reason
    // Everything except walking away hands the microphone to the assistant,
    // which is already serving. Abandonment is the one outcome after which
    // there is no conversation to be in.
    this.state.stage = reason === 'abandoned' ? 'settled' : 'serving'
    return [{ kind: 'settle', reason, profile: this.state.profile }]
  }

  // -- plumbing -------------------------------------------------------------

  private get script(): OnboardingScript {
    return this.scripts[this.state.locale]
  }

  private context(): StepContext {
    return {
      script: this.script,
      locale: this.state.locale,
      rotate: (key, variants) => this.rotate(key, variants),
      profile: this.state.profile,
    }
  }

  private rotate(key: string, variants: readonly string[]): string {
    const turn = this.state.rotations[key] ?? 0
    this.state.rotations[key] = turn + 1
    return pickVariant(variants, turn)
  }

  private say(text: string, options: { interruptible: boolean }): OnboardingEffect {
    return { kind: 'say', text, interruptible: options.interruptible }
  }

  private persist(slot: SlotId): OnboardingEffect {
    return { kind: 'persist', slot, profile: this.state.profile }
  }

  private now(): number {
    return this.options.now ? this.options.now() : Date.now()
  }
}

/**
 * Where a second run of onboarding starts.
 *
 * Not always at the beginning. Somebody who gave a language and a consent and
 * then closed the app should be asked their name, not greeted in two languages
 * again. Somebody who has no consent on file is taken straight to the open
 * conversation, because nothing they say could be written down anyway.
 */
export function entryStep(profile: Profile): OnboardingStepId {
  /*
   * Asked before any slot is looked at, because for one outcome the slots are
   * all empty and the decision was still made.
   *
   * `settle` writes `completedAt` for exactly the two outcomes the person
   * chose: they finished, or they said no. A decline then goes through
   * `profileStore`, whose whole job is to keep nothing when consent was not
   * given, so what reaches the disk is an empty profile with a timestamp on
   * it. Reading the slots alone, that is indistinguishable from a device that
   * has never been used: the next visit opened with the bilingual
   * introduction, spoke the four points again and asked for permission a
   * second time, which is precisely the nagging that declining is supposed to
   * be free of.
   *
   * So the timestamp decides. It is the one field that survives a decline and
   * it already means "onboarding is over", for both of the outcomes that set
   * it.
   */
  if (profile.completedAt !== null) return 'open'
  if (profile.language === null) return 'language'
  if (profile.consent === null) return 'consent'
  if (profile.consent.value !== true) return 'open'
  if (profile.name === null) return 'name'
  return 'open'
}
